<i18n locale="en">
{
  "error": "Failed to check usage limits",
  "content": {
    "scope": {
      "allLocations": "across all locations",
      "currentLocation": "for this location",
      "location": "for {locationName}"
    },
    "firstNBlasts": {
      "usage": "This will use 1 of your {total} monthly text message blasts {scope}.",
      "remaining": "You'll have {count} text message blast remaining for the month. | You'll have {count} text message blasts remaining for the month."
    },
    "lastBlast": "This is your last text message blast {scope} for the month. You'll be able to send {total} new text message blast starting {date}. | This is your last text message blast {scope} for the month. You'll be able to send {total} new text message blasts starting {date}.",
    "lastBlastForLocation": "This is your last text message blast for this location for the month. You'll be able to send {total} new text message blast starting {date}. | This is your last text message blast for this location for the month. You'll be able to send {total} new text message blasts starting {date}.",
    "lastBlastForLocationWarning": "After this send, text blasts across all locations will also be unavailable for the month.",
    "noBlastsLeft": {
      "warning": "You've used all of your text message blasts {scope} for the month.",
      "reset": "You'll be able to send {total} new text message blast starting {date}. | You'll be able to send {total} new text message blasts starting {date}."
    }
  }
}
</i18n>

<template>
  <div v-if="status === 'loaded'">
    <OwnType
      v-if="smsBlastsLeft > 1"
      :text="`${t('content.firstNBlasts.usage', {
        total: smsBlastsAllowedPerMonth,
        scope: blastScope,
      })} ${t('content.firstNBlasts.remaining', { count: smsBlastsLeft - 1 })}`"
    />

    <div v-else-if="smsBlastsLeft === 1" class="flex flex-col gap-2">
      <OwnType
        :text="
          locationScoped
            ? t('content.lastBlastForLocation', {
                total: smsBlastsAllowedPerMonth,
                date: resetDate,
              })
            : t('content.lastBlast', {
                total: smsBlastsAllowedPerMonth,
                date: resetDate,
                scope: blastScope,
              })
        "
      />
      <OwnType
        v-if="locationScoped"
        :text="t('content.lastBlastForLocationWarning')"
      />
    </div>

    <OwnType
      v-else
      :text="`${t('content.noBlastsLeft.warning', { scope: blastScope })} ${t(
        'content.noBlastsLeft.reset',
        {
          total: smsBlastsAllowedPerMonth,
          date: resetDate,
        }
      )}`"
    />
  </div>
  <div
    v-else-if="status === 'loading'"
    class="align-center flex-row justify-center"
  >
    <OwnProgressSpinner :size="42" />
  </div>
  <OwnType v-else :text="t('error')" />
</template>

<script lang="ts" setup>
import { Olympus } from '@owner/olympus-client'
import { addMonths, format, startOfMonth } from 'date-fns'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useApplicationScope } from '@/core/store'
import { useLocations } from '@/core/store/composables/useLocations'
import { logError } from '@/logger'
import { OlympusClient } from '@/OlympusClient'
import { OwnType } from '@/ui/OwnType'

const props = defineProps<{
  executeAt: Date
  locationId: string | null
}>()
const emit = defineEmits<{
  (event: 'update', wouldExceedLimits: boolean): void
}>()

const { t } = useI18n()

const status = ref<'loading' | 'error' | 'loaded'>('loading')
const otcSmsLimitCheck = ref<Olympus.Sms.OtcSmsLimitCheck | undefined>(
  undefined
)

const smsBlastsAllowedPerMonth = computed<number>(
  () => otcSmsLimitCheck.value?.smsBlastsAllowedPerMonth ?? 0
)

const smsBlastsSentThisMonth = computed<number>(
  () => otcSmsLimitCheck.value?.smsBlastsSentThisMonth ?? 0
)

const smsBlastsLeft = computed<number>(
  () => smsBlastsAllowedPerMonth.value - smsBlastsSentThisMonth.value
)

const resetDate = computed<string>(() => {
  const startOfNextMonth = startOfMonth(addMonths(props.executeAt, 1))

  return format(startOfNextMonth, 'MMMM d, yyyy')
})

const { getLocationNameById } = useLocations()

const blastScope = computed<string>(() => {
  if (!props.locationId) {
    return t('content.scope.allLocations')
  }

  const locationName = getLocationNameById.value(props.locationId)

  return locationName
    ? t('content.scope.location', { locationName })
    : t('content.scope.currentLocation')
})

const locationScoped = computed<boolean>(() => props.locationId != null)

const { activeBrandId } = useApplicationScope()

const checkCampaignUsageAgainstLimits = async () => {
  emit('update', true)
  status.value = 'loading'
  try {
    otcSmsLimitCheck.value =
      await OlympusClient.dashboard.v1.brands.brand.campaigns.checkOTCSmsLimits(
        {
          params: { brandId: activeBrandId.value },
          query: {
            date: (props.executeAt ?? new Date()).toISOString(),
            locationId: props.locationId ?? undefined,
          },
        }
      )

    emit('update', smsBlastsLeft.value <= 0)
    status.value = 'loaded'
  } catch (err) {
    logError('Failed to check campaign usage against limits')
    status.value = 'error'
  }
}

onMounted(checkCampaignUsageAgainstLimits)
watch(() => props.executeAt, checkCampaignUsageAgainstLimits)
watch(() => props.locationId, checkCampaignUsageAgainstLimits)
</script>
